import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';

import {
  ArrowLeft,
  Upload,
  Wrench,
  FileText,
  Settings,
  Fuel,
  Droplets,
  Filter,
  Ruler,
  Gauge,
  User,
  Image as ImageIcon,
  Truck,
  Cog,
  CircleGauge,
  Eye
} from 'lucide-react';

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from '../components/ui/tabs';

const MachineryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [machinery, setMachinery] = useState(null);
  const [selectedTechnicalPhoto, setSelectedTechnicalPhoto] = useState(null);
  const [maintenance, setMaintenance] = useState([]);
  const [dailyReports, setDailyReports] = useState([]);
  const [operators, setOperators] = useState([]);
  const [users, setUsers] = useState([]);
  const [fuelTanks, setFuelTanks] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportDetailOpen, setReportDetailOpen] = useState(false);
  const [loadingReportDetail, setLoadingReportDetail] = useState(false);
  const [selectedMaintenance, setSelectedMaintenance] = useState(null);
  const [maintenanceDetailOpen, setMaintenanceDetailOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingTechnicalPhoto, setUploadingTechnicalPhoto] = useState(null);

  useEffect(() => {
    fetchMachineryDetails();
  }, [id]);

  const fetchMachineryDetails = async () => {
    try {
      const [
        machineryRes,
        maintenanceRes,
        reportsRes,
        operatorsRes,
        fuelTanksRes,
        usersRes
      ] = await Promise.all([
        axios.get(`${API}/machinery/${id}`),
        axios.get(`${API}/maintenance?machinery_id=${id}`),
        axios.get(`${API}/daily-reports?machinery_id=${id}`),
        axios.get(`${API}/operators`),
        axios.get(`${API}/fuel/tanks`),
        axios.get(`${API}/users`)
      ]);

      setMachinery(machineryRes.data);
      setMaintenance(maintenanceRes.data);
      setDailyReports(reportsRes.data);
      setOperators(operatorsRes.data);
      setFuelTanks(fuelTanksRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error(error);
      toast.error('Error al cargar detalles');
    } finally {
      setLoading(false);
    }
  };

  const getOperatorName = (operatorId) => {
    if (!operatorId) return '—';

    const user = users.find(
      (item) => String(item.id) === String(operatorId)
    );

    return user?.name || operatorId;
  };

  const getFuelTankName = (tankId) => {
    if (!tankId) return '—';

    const tank = fuelTanks.find(
      (item) => String(item.id) === String(tankId)
    );

    return tank?.name || tankId;
  };

  const handleViewMaintenance = (maintenanceItem) => {
    if (!maintenanceItem) return;

    setSelectedMaintenance(maintenanceItem);
    setMaintenanceDetailOpen(true);
  };

  const handleViewReport = async (report) => {
    if (!report?.id) return;

    setLoadingReportDetail(true);
    setReportDetailOpen(true);
    setSelectedReport(null);

    try {
      const response = await axios.get(
        `${API}/daily-reports/${report.id}`
      );

      setSelectedReport(response.data);
    } catch (error) {
      console.error('Error cargando detalle del parte:', error);
      toast.error('No se pudo cargar el detalle del parte');
      setReportDetailOpen(false);
    } finally {
      setLoadingReportDetail(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Seleccione una imagen válida');
      return;
    }

    setUploadingImage(true);

    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(
        `${API}/machinery/${id}/images`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast.success('Imagen subida exitosamente');
      await fetchMachineryDetails();
    } catch (error) {
      console.error(error);
      toast.error('Error al subir imagen');
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const handleTechnicalPhotoUpload = async (photoType, e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Seleccione una imagen válida');
      e.target.value = '';
      return;
    }

    setUploadingTechnicalPhoto(photoType);

    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(
        `${API}/machinery/${id}/technical-photo/${photoType}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast.success('Fotografía técnica guardada');
      await fetchMachineryDetails();
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.detail ||
        'Error al subir fotografía técnica'
      );
    } finally {
      setUploadingTechnicalPhoto(null);
      e.target.value = '';
    }
  };

  // Resolver fotografías técnicas almacenadas como rutas físicas.
  // También mantiene compatibilidad con Data URI antiguos.
  const getTechnicalPhotoUrl = (photo) => {
    if (!photo) return photo;

    if (photo.startsWith("/uploads/")) {
      return `${process.env.REACT_APP_BACKEND_URL}${photo}`;
    }

    return photo;
  };

  const technicalPhotoItems = [
    {
      key: 'main',
      title: 'Foto principal',
      description: 'Vista general del equipo'
    },
    {
      key: 'serial_number',
      title: 'Número de serie',
      description: 'Placa o identificación de serie'
    },
    {
      key: 'engine_oil',
      title: 'Aceite de motor',
      description: 'Envase o etiqueta del aceite utilizado'
    },
    {
      key: 'engine_oil_filter',
      title: 'Filtro de aceite de motor',
      description: 'Filtro, código y referencia'
    },
    {
      key: 'fuel_filter',
      title: 'Filtro de combustible',
      description: 'Filtro, código y referencia'
    },
    {
      key: 'air_filter',
      title: 'Filtro de aire',
      description: 'Filtro, código y referencia'
    },
    {
      key: 'hydraulic_filter',
      title: 'Filtro hidráulico',
      description: 'Filtro, código y referencia'
    },
    {
      key: 'transmission_oil',
      title: 'Aceite de caja / convertidor',
      description: 'Envase o etiqueta del producto'
    },
    {
      key: 'differential_oil',
      title: 'Aceite de diferencial / mando',
      description: 'Envase o etiqueta del producto'
    },
    {
      key: 'hydraulic_oil',
      title: 'Aceite hidráulico',
      description: 'Envase o etiqueta del producto'
    },
    {
      key: 'water_pump_belt',
      title: 'Correa de bomba de agua',
      description: 'Correa, código y referencia'
    },
    {
      key: 'alternator_belt',
      title: 'Correa de alternador',
      description: 'Correa, código y referencia'
    }
  ];

  if (loading) {
    return (
      <div className="text-center py-12 text-slate-500">
        Cargando maquinaria...
      </div>
    );
  }

  if (!machinery) {
    return (
      <div className="text-center py-12 text-slate-500">
        Maquinaria no encontrada
      </div>
    );
  }

  const formatNumber = (value, decimals = 2) => {
    if (value === null || value === undefined || value === '') {
      return '—';
    }

    const number = Number(value);

    if (Number.isNaN(number)) {
      return value;
    }

    return number.toLocaleString('es-BO', {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals
    });
  };

  const fuelLabels = {
    diesel: 'Diésel',
    gasoline: 'Gasolina',
    gas: 'Gas / GNV',
    lpg: 'GLP',
    electric: 'Eléctrico',
    hybrid: 'Híbrido',
    other: 'Otro'
  };

  const statusLabels = {
    operational: 'Operacional',
    maintenance: 'Mantenimiento',
    inactive: 'Inactivo'
  };

  const statusLabel =
    statusLabels[machinery.status] || machinery.status || '—';

  const fuelLabel =
    fuelLabels[machinery.fuel_type] ||
    machinery.fuel_type ||
    '—';

  const InfoItem = ({
    label,
    value,
    mono = false
  }) => (
    <div className="bg-slate-50 rounded-md p-3">
      <p className="text-xs text-slate-500 mb-1">
        {label}
      </p>

      <p
        className={`font-medium text-slate-800 ${
          mono ? 'font-mono' : ''
        }`}
      >
        {value === null ||
        value === undefined ||
        value === ''
          ? '—'
          : value}
      </p>
    </div>
  );

  const SectionTitle = ({
    icon: Icon,
    title,
    description
  }) => (
    <div className="flex items-center gap-3 mb-5">
      <div className="w-10 h-10 rounded-md bg-orange-50 flex items-center justify-center">
        <Icon size={20} className="text-orange-600" />
      </div>

      <div>
        <h3 className="text-lg font-semibold text-slate-800">
          {title}
        </h3>

        {description && (
          <p className="text-sm text-slate-500">
            {description}
          </p>
        )}
      </div>
    </div>
  );

  const FluidCard = ({
    title,
    data
  }) => (
    <div className="border border-slate-200 rounded-md p-4">
      <h4 className="font-semibold text-slate-800 mb-3">
        {title}
      </h4>

      {data ? (
        <div className="grid grid-cols-2 gap-3">
          <InfoItem
            label="Cantidad"
            value={
              data.quantity !== undefined
                ? `${formatNumber(data.quantity)} ${data.unit || 'L'}`
                : '—'
            }
          />

          <InfoItem
            label="Tipo"
            value={data.type}
          />

          <InfoItem
            label="Marca"
            value={data.brand}
          />

          <InfoItem
            label="Especificación"
            value={
              data.specification ||
              data.spec ||
              data.api
            }
          />
        </div>
      ) : (
        <p className="text-sm text-slate-500">
          Sin información registrada.
        </p>
      )}
    </div>
  );

  const FilterCard = ({
    title,
    data
  }) => (
    <div className="border border-slate-200 rounded-md p-4">
      <h4 className="font-semibold text-slate-800 mb-3">
        {title}
      </h4>

      {data ? (
        <div className="grid grid-cols-2 gap-3">
          <InfoItem
            label="Serie / Código"
            value={
              data.serial ||
              data.series ||
              data.code
            }
          />

          <InfoItem
            label="Marca"
            value={data.brand}
          />

          <InfoItem
            label="Proveedor"
            value={data.supplier}
          />
        </div>
      ) : (
        <p className="text-sm text-slate-500">
          Sin información registrada.
        </p>
      )}
    </div>
  );

  const BeltCard = ({
    title,
    data
  }) => (
    <div className="border border-slate-200 rounded-md p-4">
      <h4 className="font-semibold text-slate-800 mb-3">
        {title}
      </h4>

      {data ? (
        <div className="grid grid-cols-2 gap-3">
          <InfoItem
            label="Serie / Código"
            value={
              data.serial ||
              data.series ||
              data.code
            }
          />

          <InfoItem
            label="Marca"
            value={data.brand}
          />

          <InfoItem
            label="Proveedor"
            value={data.supplier}
          />
        </div>
      ) : (
        <p className="text-sm text-slate-500">
          Sin información registrada.
        </p>
      )}
    </div>
  );

  return (
    <div
      data-testid="machinery-detail-page"
      className="space-y-6"
    >
      {/* BACK */}
      <button
        data-testid="back-button"
        onClick={() => navigate('/machinery')}
        className="flex items-center text-slate-600 hover:text-slate-800"
      >
        <ArrowLeft
          size={20}
          className="mr-2"
        />
        Volver a Maquinaria
      </button>

      {/* HEADER */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="p-6">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">

            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3 mb-2">

                <h1
                  className="text-3xl font-bold text-slate-800"
                  style={{
                    fontFamily: 'Barlow Condensed, sans-serif'
                  }}
                >
                  {machinery.name}
                </h1>

                <span
                  className={`status-badge status-${machinery.status}`}
                >
                  {statusLabel}
                </span>
              </div>

              <p className="text-slate-500 mb-5">
                {machinery.brand} {machinery.model}
                {machinery.year
                  ? ` · ${machinery.year}`
                  : ''}
              </p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">

                <InfoItem
                  label="Tipo"
                  value={machinery.type}
                />

                <InfoItem
                  label="Serie"
                  value={machinery.serial_number}
                  mono
                />

                <InfoItem
                  label="Horómetro"
                  value={`${formatNumber(machinery.engine_hours, 1)} h`}
                  mono
                />

                <InfoItem
                  label="Combustible"
                  value={fuelLabel}
                />

              </div>
            </div>

            {/* FOTO PRINCIPAL */}
            <div className="w-full lg:w-72">

              {machinery.images &&
              machinery.images.length > 0 ? (
                <img
                  src={machinery.images[0].startsWith("/uploads/")
                    ? `${process.env.REACT_APP_BACKEND_URL}${machinery.images[0]}`
                    : machinery.images[0]}
                  alt={machinery.name}
                  className="w-full h-48 object-cover rounded-md border border-slate-200"
                />
              ) : (
                <div className="w-full h-48 rounded-md bg-slate-100 border border-slate-200 flex flex-col items-center justify-center text-slate-400">
                  <ImageIcon size={36} />
                  <span className="text-sm mt-2">
                    Sin fotografía principal
                  </span>
                </div>
              )}

            </div>

          </div>
        </div>
      </div>

      {/* TABS */}
      <Tabs
        defaultValue="general"
        className="bg-white border border-slate-200 rounded-md"
      >

        <TabsList className="w-full justify-start overflow-x-auto border-b border-slate-200 rounded-none bg-slate-50">

          <TabsTrigger
            value="general"
            className="data-[state=active]:border-b-2 data-[state=active]:border-orange-500"
          >
            <Settings size={15} className="mr-2" />
            General
          </TabsTrigger>

          <TabsTrigger
            value="engine"
            className="data-[state=active]:border-b-2 data-[state=active]:border-orange-500"
          >
            <Cog size={15} className="mr-2" />
            Motor
          </TabsTrigger>

          <TabsTrigger
            value="fuel"
            className="data-[state=active]:border-b-2 data-[state=active]:border-orange-500"
          >
            <Fuel size={15} className="mr-2" />
            Combustible
          </TabsTrigger>

          <TabsTrigger
            value="lubricants"
            className="data-[state=active]:border-b-2 data-[state=active]:border-orange-500"
          >
            <Droplets size={15} className="mr-2" />
            Lubricantes
          </TabsTrigger>

          <TabsTrigger
            value="filters"
            className="data-[state=active]:border-b-2 data-[state=active]:border-orange-500"
          >
            <Filter size={15} className="mr-2" />
            Filtros
          </TabsTrigger>

          <TabsTrigger
            value="belts"
            className="data-[state=active]:border-b-2 data-[state=active]:border-orange-500"
          >
            <Cog size={15} className="mr-2" />
            Correas
          </TabsTrigger>

          <TabsTrigger
            value="dimensions"
            className="data-[state=active]:border-b-2 data-[state=active]:border-orange-500"
          >
            <Ruler size={15} className="mr-2" />
            Dimensiones
          </TabsTrigger>

          <TabsTrigger
            value="operation"
            className="data-[state=active]:border-b-2 data-[state=active]:border-orange-500"
          >
            <User size={15} className="mr-2" />
            Operación
          </TabsTrigger>

          <TabsTrigger
            value="maintenance"
            className="data-[state=active]:border-b-2 data-[state=active]:border-orange-500"
          >
            <Wrench size={15} className="mr-2" />
            Mantenimiento
          </TabsTrigger>

          <TabsTrigger
            value="reports"
            className="data-[state=active]:border-b-2 data-[state=active]:border-orange-500"
          >
            <FileText size={15} className="mr-2" />
            Partes
          </TabsTrigger>

          <TabsTrigger
            value="technical-photos"
            className="data-[state=active]:border-b-2 data-[state=active]:border-orange-500"
          >
            <ImageIcon size={15} className="mr-2" />
            Fotos técnicas
          </TabsTrigger>

          <TabsTrigger
            value="gallery"
            className="data-[state=active]:border-b-2 data-[state=active]:border-orange-500"
          >
            <ImageIcon size={15} className="mr-2" />
            Fotos
          </TabsTrigger>

        </TabsList>

        {/* =========================
            GENERAL
        ========================= */}
        <TabsContent
          value="general"
          className="p-6"
        >
          <SectionTitle
            icon={Settings}
            title="Información general"
            description="Identificación y estado del equipo"
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

            <InfoItem
              label="Código interno"
              value={machinery.internal_code}
            />

            <InfoItem
              label="Nombre del equipo"
              value={machinery.name}
            />

            <InfoItem
              label="Tipo"
              value={machinery.type}
            />

            <InfoItem
              label="Marca"
              value={machinery.brand}
            />

            <InfoItem
              label="Modelo"
              value={machinery.model}
            />

            <InfoItem
              label="Año"
              value={machinery.year}
            />

            <InfoItem
              label="Número de serie"
              value={machinery.serial_number}
              mono
            />

            <InfoItem
              label="Chasis / VIN"
              value={machinery.chassis_vin}
              mono
            />

            <InfoItem
              label="Estado"
              value={statusLabel}
            />

          </div>

          {machinery.notes && (
            <div className="mt-5 pt-5 border-t border-slate-200">
              <p className="text-sm text-slate-500 mb-1">
                Notas
              </p>

              <p className="text-slate-800">
                {machinery.notes}
              </p>
            </div>
          )}
        </TabsContent>

        {/* =========================
            MOTOR
        ========================= */}
        <TabsContent
          value="engine"
          className="p-6"
        >
          <SectionTitle
            icon={Cog}
            title="Motor"
            description="Características técnicas del motor"
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

            <InfoItem
              label="Marca del motor"
              value={machinery.engine_brand}
            />

            <InfoItem
              label="Modelo del motor"
              value={machinery.engine_model}
            />

            <InfoItem
              label="Serie del motor"
              value={machinery.engine_serial}
              mono
            />

            <InfoItem
              label="Configuración"
              value={machinery.engine_configuration}
            />

            <InfoItem
              label="Cilindrada"
              value={
                machinery.engine_displacement
                  ? `${formatNumber(machinery.engine_displacement)} ${machinery.engine_displacement_unit || 'L'}`
                  : '—'
              }
            />

            <InfoItem
              label="Potencia"
              value={
                machinery.engine_hp
                  ? `${formatNumber(machinery.engine_hp)} HP`
                  : '—'
              }
            />

            <InfoItem
              label="Turbo"
              value={
                machinery.has_turbo
                  ? 'Sí'
                  : 'No'
              }
            />

            <InfoItem
              label="Horómetro"
              value={`${formatNumber(machinery.engine_hours, 1)} h`}
              mono
            />

          </div>
        </TabsContent>

        {/* =========================
            COMBUSTIBLE
        ========================= */}
        <TabsContent
          value="fuel"
          className="p-6"
        >
          <SectionTitle
            icon={Fuel}
            title="Combustible"
            description="Consumo y capacidad del sistema de combustible"
          />

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">

            <InfoItem
              label="Tipo de combustible"
              value={fuelLabel}
            />

            <InfoItem
              label="Capacidad del tanque"
              value={
                machinery.fuel_tank_capacity
                  ? `${formatNumber(machinery.fuel_tank_capacity)} ${machinery.fuel_tank_unit || 'L'}`
                  : '—'
              }
            />

            <InfoItem
              label="Consumo promedio"
              value={
                machinery.fuel_consumption
                  ? `${formatNumber(machinery.fuel_consumption)} ${machinery.fuel_consumption_unit || 'L/h'}`
                  : '—'
              }
            />

            <InfoItem
              label="Odómetro"
              value={
                machinery.odometer
                  ? formatNumber(machinery.odometer)
                  : '—'
              }
            />

          </div>
        </TabsContent>

        {/* =========================
            LUBRICANTES
        ========================= */}
        <TabsContent
          value="lubricants"
          className="p-6"
        >
          <SectionTitle
            icon={Droplets}
            title="Lubricantes"
            description="Aceites y fluidos especificados para el equipo"
          />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

            <FluidCard
              title="Aceite de motor"
              data={machinery.engine_oil}
            />

            <FluidCard
              title="Aceite de caja / convertidor"
              data={machinery.transmission_oil}
            />

            <FluidCard
              title="Aceite de diferencial / mando"
              data={machinery.differential_oil}
            />

            <FluidCard
              title="Aceite hidráulico"
              data={machinery.hydraulic_oil}
            />

          </div>

          {/* Compatibilidad con datos antiguos */}
          {machinery.oil_type && (
            <div className="mt-5 p-4 bg-slate-50 rounded-md">
              <p className="text-sm text-slate-500">
                Registro anterior de aceite de motor
              </p>

              <p className="font-medium text-slate-800">
                {machinery.oil_type}
                {machinery.oil_capacity
                  ? ` · ${formatNumber(machinery.oil_capacity)} L`
                  : ''}
              </p>
            </div>
          )}

          {machinery.hydraulic_oil_type && (
            <div className="mt-3 p-4 bg-slate-50 rounded-md">
              <p className="text-sm text-slate-500">
                Registro anterior de aceite hidráulico
              </p>

              <p className="font-medium text-slate-800">
                {machinery.hydraulic_oil_type}
                {machinery.hydraulic_oil_capacity
                  ? ` · ${formatNumber(machinery.hydraulic_oil_capacity)} L`
                  : ''}
              </p>
            </div>
          )}
        </TabsContent>

        {/* =========================
            FILTROS
        ========================= */}
        <TabsContent
          value="filters"
          className="p-6"
        >
          <SectionTitle
            icon={Filter}
            title="Filtros"
            description="Filtros instalados y proveedores"
          />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

            <FilterCard
              title="Filtro de combustible"
              data={machinery.fuel_filter}
            />

            <FilterCard
              title="Filtro de aceite de motor"
              data={machinery.engine_oil_filter}
            />

            <FilterCard
              title="Filtro de aire"
              data={machinery.air_filter}
            />

            <FilterCard
              title="Filtro hidráulico"
              data={machinery.hydraulic_filter}
            />

          </div>

          {machinery.filter_codes && (
            <div className="mt-5 p-4 bg-slate-50 rounded-md">
              <p className="text-sm text-slate-500">
                Códigos de filtros registrados anteriormente
              </p>

              <p className="font-medium text-slate-800">
                {machinery.filter_codes}
              </p>
            </div>
          )}
        </TabsContent>

        {/* =========================
            CORREAS
        ========================= */}
        <TabsContent
          value="belts"
          className="p-6"
        >
          <SectionTitle
            icon={Cog}
            title="Correas"
            description="Correas y referencias del equipo"
          />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

            <BeltCard
              title="Correa de bomba de agua"
              data={machinery.water_pump_belt}
            />

            <BeltCard
              title="Correa de alternador"
              data={machinery.alternator_belt}
            />

            {machinery.other_belts &&
              machinery.other_belts.map((belt, index) => (
                <BeltCard
                  key={index}
                  title={`Otra correa ${index + 1}`}
                  data={belt}
                />
              ))}

          </div>

          {machinery.belt_codes && (
            <div className="mt-5 p-4 bg-slate-50 rounded-md">
              <p className="text-sm text-slate-500">
                Códigos de correas registrados anteriormente
              </p>

              <p className="font-medium text-slate-800">
                {machinery.belt_codes}
              </p>
            </div>
          )}
        </TabsContent>

        {/* =========================
            DIMENSIONES
        ========================= */}
        <TabsContent
          value="dimensions"
          className="p-6"
        >
          <SectionTitle
            icon={Ruler}
            title="Dimensiones y transporte"
            description="Características físicas del equipo"
          />

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">

            <InfoItem
              label="Peso"
              value={
                machinery.machine_weight
                  ? `${formatNumber(machinery.machine_weight)} kg`
                  : '—'
              }
            />

            <InfoItem
              label="Longitud de transporte"
              value={
                machinery.transport_length
                  ? `${formatNumber(machinery.transport_length)} m`
                  : '—'
              }
            />

            <InfoItem
              label="Ancho de transporte"
              value={
                machinery.transport_width
                  ? `${formatNumber(machinery.transport_width)} m`
                  : '—'
              }
            />

            <InfoItem
              label="Altura de transporte"
              value={
                machinery.transport_height
                  ? `${formatNumber(machinery.transport_height)} m`
                  : '—'
              }
            />

          </div>
        </TabsContent>

        {/* =========================
            OPERACIÓN
        ========================= */}
        <TabsContent
          value="operation"
          className="p-6"
        >
          <SectionTitle
            icon={Gauge}
            title="Operación"
            description="Responsabilidad, ubicación y capacidad de trabajo"
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

            <InfoItem
              label="Responsable"
              value={machinery.responsible}
            />

            <InfoItem
              label="Ubicación"
              value={machinery.location}
            />

            <InfoItem
              label="Proyecto"
              value={machinery.project}
            />

            <InfoItem
              label="Estado"
              value={statusLabel}
            />

            <InfoItem
              label="Capacidad de trabajo"
              value={
                machinery.work_capacity
                  ? `${formatNumber(machinery.work_capacity)} ${machinery.work_capacity_unit || ''}`
                  : '—'
              }
            />

            <InfoItem
              label="Horas de motor"
              value={`${formatNumber(machinery.engine_hours, 1)} h`}
            />

          </div>
        </TabsContent>

        {/* =========================
            MANTENIMIENTO
        ========================= */}
        <TabsContent
          value="maintenance"
          className="p-6"
        >
          {maintenance.length === 0 ? (
            <div className="text-center py-10">
              <Wrench
                size={36}
                className="mx-auto text-slate-300 mb-3"
              />

              <p className="text-slate-500">
                No hay mantenimientos registrados
              </p>
            </div>
          ) : (
            <div className="space-y-4">

              {maintenance.map((m) => (
                <div
                  key={m.id}
                  className="border border-slate-200 rounded-md p-4"
                  data-testid={`maintenance-item-${m.id}`}
                >

                  <div className="flex items-start justify-between">

                    <div className="flex-1">

                      <div className="flex items-center gap-3 mb-2">

                        <span
                          className={`status-badge status-${m.status}`}
                        >
                          {m.status === 'pending'
                            ? 'Pendiente'
                            : 'Completado'}
                        </span>

                        <span className="text-sm text-slate-600">
                          {m.type}
                        </span>

                      </div>

                      <p className="text-slate-800 mb-2">
                        {m.description}
                      </p>

                      <div className="flex flex-wrap gap-4 text-sm text-slate-600">

                        {m.scheduled_date && (
                          <span>
                            Programado:{' '}
                            {new Date(
                              m.scheduled_date
                            ).toLocaleDateString()}
                          </span>
                        )}

                        {m.completed_date && (
                          <span>
                            Completado:{' '}
                            {new Date(
                              m.completed_date
                            ).toLocaleDateString()}
                          </span>
                        )}

                        {m.cost !== undefined &&
                          m.cost !== null && (
                            <span className="font-mono">
                              Bs. {formatNumber(m.cost)}
                            </span>
                          )}

                      </div>

                      <div className="mt-4 pt-3 border-t border-slate-100 flex justify-end">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewMaintenance(m)}
                          className="h-8 px-3"
                        >
                          <Eye size={15} className="mr-1.5" />
                          Ver mantenimiento
                        </Button>
                      </div>

                    </div>

                  </div>

                </div>
              ))}

            </div>
          )}
        </TabsContent>

        {/* =========================
            DETALLE DE MANTENIMIENTO
        ========================= */}
        <Dialog
          open={maintenanceDetailOpen}
          onOpenChange={setMaintenanceDetailOpen}
        >
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Wrench size={20} />
                Detalle del mantenimiento
              </DialogTitle>
              <DialogDescription>
                Información completa del mantenimiento registrado
              </DialogDescription>
            </DialogHeader>

            {selectedMaintenance && (
              <div className="space-y-5">

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                    <p className="text-xs text-slate-500">Estado</p>
                    <p className="font-semibold text-slate-800 mt-1">
                      {selectedMaintenance.status === 'pending'
                        ? 'Pendiente'
                        : selectedMaintenance.status === 'completed'
                          ? 'Completado'
                          : selectedMaintenance.status || '—'}
                    </p>
                  </div>

                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                    <p className="text-xs text-slate-500">Tipo</p>
                    <p className="font-semibold text-slate-800 mt-1">
                      {selectedMaintenance.type || '—'}
                    </p>
                  </div>

                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                    <p className="text-xs text-slate-500">Prioridad</p>
                    <p className="font-semibold text-slate-800 mt-1">
                      {selectedMaintenance.priority || '—'}
                    </p>
                  </div>
                </div>

                <div className="rounded-lg border border-slate-200 p-4">
                  <p className="text-xs text-slate-500 mb-1">Descripción</p>
                  <p className="text-slate-800">
                    {selectedMaintenance.description || '—'}
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-800 mb-3">
                    Fechas y costo
                  </h4>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="rounded-lg border border-slate-200 p-3">
                      <p className="text-xs text-slate-500">Programado</p>
                      <p className="font-medium mt-1">
                        {selectedMaintenance.scheduled_date
                          ? new Date(selectedMaintenance.scheduled_date).toLocaleString('es-BO')
                          : '—'}
                      </p>
                    </div>

                    <div className="rounded-lg border border-slate-200 p-3">
                      <p className="text-xs text-slate-500">Completado</p>
                      <p className="font-medium mt-1">
                        {selectedMaintenance.completed_date
                          ? new Date(selectedMaintenance.completed_date).toLocaleString('es-BO')
                          : '—'}
                      </p>
                    </div>

                    <div className="rounded-lg border border-slate-200 p-3">
                      <p className="text-xs text-slate-500">Costo</p>
                      <p className="font-semibold text-slate-800 mt-1">
                        Bs. {formatNumber(selectedMaintenance.cost || 0)}
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-800 mb-3">
                    Trabajo realizado
                  </h4>

                  <div className="space-y-3">
                    <div className="rounded-lg border border-slate-200 p-4">
                      <p className="text-xs text-slate-500">Trabajo de taller</p>
                      <p className="text-slate-800 mt-1">
                        {selectedMaintenance.workshop_work || '—'}
                      </p>
                    </div>

                    <div className="rounded-lg border border-slate-200 p-4">
                      <p className="text-xs text-slate-500">Trabajo realizado</p>
                      <p className="text-slate-800 mt-1">
                        {selectedMaintenance.work_performed || '—'}
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-800 mb-3">
                    Aceites y fluidos
                  </h4>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="rounded-lg border border-slate-200 p-4">
                      <p className="text-xs text-slate-500">Aceite de motor</p>
                      <p className="font-medium mt-1">
                        {selectedMaintenance.oil_changed ? 'Cambio realizado' : 'Sin cambio'}
                      </p>
                      {selectedMaintenance.oil_type && (
                        <p className="text-sm text-slate-600 mt-1">
                          Tipo: {selectedMaintenance.oil_type}
                        </p>
                      )}
                      {selectedMaintenance.oil_quantity !== null &&
                        selectedMaintenance.oil_quantity !== undefined && (
                          <p className="text-sm text-slate-600">
                            Cantidad: {formatNumber(selectedMaintenance.oil_quantity, 2)} L
                          </p>
                        )}
                    </div>

                    <div className="rounded-lg border border-slate-200 p-4">
                      <p className="text-xs text-slate-500">Aceite hidráulico</p>
                      <p className="font-medium mt-1">
                        {selectedMaintenance.hydraulic_oil_changed
                          ? 'Cambio realizado'
                          : 'Sin cambio'}
                      </p>
                      {selectedMaintenance.hydraulic_oil_type && (
                        <p className="text-sm text-slate-600 mt-1">
                          Tipo: {selectedMaintenance.hydraulic_oil_type}
                        </p>
                      )}
                      {selectedMaintenance.hydraulic_oil_quantity !== null &&
                        selectedMaintenance.hydraulic_oil_quantity !== undefined && (
                          <p className="text-sm text-slate-600">
                            Cantidad: {formatNumber(selectedMaintenance.hydraulic_oil_quantity, 2)} L
                          </p>
                        )}
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-800 mb-3">
                    Filtros
                  </h4>

                  <div className="rounded-lg border border-slate-200 p-4">
                    <p className="text-slate-800">
                      {selectedMaintenance.filters_changed || '—'}
                    </p>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-800 mb-3">
                    Personal y tiempos
                  </h4>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="rounded-lg border border-slate-200 p-4">
                      <p className="text-xs text-slate-500">Técnico principal</p>
                      <p className="font-medium mt-1">
                        {selectedMaintenance.main_technician || '—'}
                      </p>
                    </div>

                    <div className="rounded-lg border border-slate-200 p-4">
                      <p className="text-xs text-slate-500">Ayudante</p>
                      <p className="font-medium mt-1">
                        {selectedMaintenance.assistant_technician || '—'}
                      </p>
                    </div>

                    <div className="rounded-lg border border-slate-200 p-4">
                      <p className="text-xs text-slate-500">Entrega</p>
                      <p className="font-medium mt-1">
                        {selectedMaintenance.delivery_datetime
                          ? new Date(selectedMaintenance.delivery_datetime).toLocaleString('es-BO')
                          : '—'}
                      </p>
                    </div>

                    <div className="rounded-lg border border-slate-200 p-4">
                      <p className="text-xs text-slate-500">Recepción</p>
                      <p className="font-medium mt-1">
                        {selectedMaintenance.reception_datetime
                          ? new Date(selectedMaintenance.reception_datetime).toLocaleString('es-BO')
                          : '—'}
                      </p>
                    </div>

                    <div className="rounded-lg border border-slate-200 p-4">
                      <p className="text-xs text-slate-500">Horas de parada</p>
                      <p className="font-medium mt-1">
                        {selectedMaintenance.downtime_hours !== null &&
                        selectedMaintenance.downtime_hours !== undefined
                          ? `${formatNumber(selectedMaintenance.downtime_hours, 2)} h`
                          : '—'}
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-800 mb-3">
                    Notas
                  </h4>

                  <div className="space-y-3">
                    <div className="rounded-lg border border-slate-200 p-4">
                      <p className="text-xs text-slate-500">Notas generales</p>
                      <p className="text-slate-800 mt-1">
                        {selectedMaintenance.notes || '—'}
                      </p>
                    </div>

                    <div className="rounded-lg border border-slate-200 p-4">
                      <p className="text-xs text-slate-500">Notas del técnico</p>
                      <p className="text-slate-800 mt-1">
                        {selectedMaintenance.technician_notes || '—'}
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-800 mb-3">
                    Repuestos utilizados
                  </h4>

                  {Array.isArray(selectedMaintenance.spare_parts_used) &&
                  selectedMaintenance.spare_parts_used.length > 0 ? (
                    <div className="overflow-x-auto rounded-lg border border-slate-200">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="text-left p-3">Repuesto</th>
                            <th className="text-left p-3">Cantidad</th>
                            <th className="text-left p-3">Detalle</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedMaintenance.spare_parts_used.map((part, index) => (
                            <tr key={index} className="border-t border-slate-100">
                              <td className="p-3 font-medium">
                                {part.name || part.part_name || '—'}
                              </td>
                              <td className="p-3">
                                {part.quantity ?? '—'}
                              </td>
                              <td className="p-3 text-slate-600">
                                {part.part_number || part.description || '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="rounded-lg border border-dashed border-slate-300 p-4 text-sm text-slate-500">
                      No se registraron repuestos utilizados.
                    </div>
                  )}
                </div>

                <div className="text-xs text-slate-400 border-t border-slate-100 pt-3">
                  ID del mantenimiento: {selectedMaintenance.id || '—'}
                </div>

              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* =========================
            PARTES DIARIOS
        ========================= */}
        <TabsContent
          value="reports"
          className="p-6"
        >
          {dailyReports.length === 0 ? (
            <div className="text-center py-10">
              <FileText
                size={36}
                className="mx-auto text-slate-300 mb-3"
              />

              <p className="text-slate-500">
                No hay partes diarios registrados
              </p>
            </div>
          ) : (
            <div className="space-y-4">

              {dailyReports.slice(0, 20).map((r) => (
                <div
                  key={r.id}
                  className="border border-slate-200 rounded-md p-4"
                  data-testid={`report-item-${r.id}`}
                >

                  <div className="flex items-start justify-between mb-2 gap-3">

                    <span className="font-medium">
                      {new Date(
                        r.date
                      ).toLocaleDateString()}
                    </span>

                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm">
                        {formatNumber(
                          r.hours_worked,
                          1
                        )}{' '}
                        hrs
                      </span>

                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => handleViewReport(r)}
                        className="h-8 px-3"
                      >
                        <Eye size={15} className="mr-1.5" />
                        Ver parte
                      </Button>
                    </div>

                  </div>

                  <p className="text-sm text-slate-800 mb-3">
                    {r.work_description}
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs text-slate-600">

                    <div>
                      Combustible:{' '}
                      <span className="font-mono">
                        {r.diesel_consumed ?? 0} L
                      </span>
                    </div>

                    <div>
                      Aceite:{' '}
                      <span className="font-mono">
                        {r.oil_consumed ?? 0} L
                      </span>
                    </div>

                    <div>
                      Ubicación:{' '}
                      {r.location || '—'}
                    </div>

                  </div>

                </div>
              ))}

            </div>
          )}
        </TabsContent>

        {/* =========================
            FOTOS TÉCNICAS
        ========================= */}
        <TabsContent
          value="technical-photos"
          className="p-6"
        >
          <SectionTitle
            icon={ImageIcon}
            title="Documentación fotográfica técnica"
            description="Fotografías identificadas de componentes y referencias del equipo"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">

            {technicalPhotoItems.map((item) => {
              const photo =
                machinery.technical_photos?.[item.key];

              const isUploading =
                uploadingTechnicalPhoto === item.key;

              return (
                <div
                  key={item.key}
                  className="border border-slate-200 rounded-md overflow-hidden bg-white"
                >

                  <div className="p-4 border-b border-slate-200">
                    <h4 className="font-semibold text-slate-800">
                      {item.title}
                    </h4>

                    <p className="text-xs text-slate-500 mt-1">
                      {item.description}
                    </p>
                  </div>

                  {photo ? (
                    <div className="p-3">
                      <button
                        type="button"
                        onClick={() =>
                          setSelectedTechnicalPhoto({
                            url: photo,
                            title: item.title
                          })
                        }
                        className="group relative block w-full cursor-zoom-in"
                        title="Ver fotografía en grande"
                      >
                        <img
                          src={getTechnicalPhotoUrl(photo)}
                          alt={item.title}
                          className="w-full h-48 object-cover rounded-md border border-slate-200 transition-transform duration-200 group-hover:scale-[1.02]"
                        />

                        <div className="absolute inset-0 flex items-center justify-center rounded-md bg-black/0 group-hover:bg-black/25 transition-colors">
                          <span className="opacity-0 group-hover:opacity-100 bg-black/70 text-white px-3 py-2 rounded-lg text-sm font-medium transition-opacity">
                            🔍 Ver en grande
                          </span>
                        </div>
                      </button>
                    </div>
                  ) : (
                    <div className="mx-3 mt-3 h-48 rounded-md bg-slate-50 border border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400">
                      <ImageIcon size={36} />
                      <span className="text-xs mt-2">
                        Sin fotografía
                      </span>
                    </div>
                  )}

                  <div className="p-3">

                    <label
                      className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-md bg-slate-800 text-white text-sm font-medium cursor-pointer hover:bg-slate-700 transition-colors"
                    >
                      <Upload size={16} />

                      {isUploading
                        ? 'Subiendo...'
                        : photo
                          ? 'Reemplazar fotografía'
                          : 'Subir fotografía'}

                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) =>
                          handleTechnicalPhotoUpload(
                            item.key,
                            e
                          )
                        }
                        disabled={isUploading}
                        className="hidden"
                      />
                    </label>

                  </div>

                </div>
              );
            })}

          </div>

        </TabsContent>

        {selectedTechnicalPhoto && (
          <div
            className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-3 sm:p-6"
            onClick={() => setSelectedTechnicalPhoto(null)}
          >
            <div
              className="relative w-full h-full flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => setSelectedTechnicalPhoto(null)}
                className="absolute top-2 right-2 sm:top-4 sm:right-4 z-10 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center text-2xl transition-colors"
                aria-label="Cerrar fotografía"
              >
                ×
              </button>

              <div className="max-w-7xl max-h-[92vh] w-full flex flex-col items-center justify-center">
                <img
                  src={getTechnicalPhotoUrl(selectedTechnicalPhoto.url)}
                  alt={selectedTechnicalPhoto.title}
                  className="max-w-full max-h-[82vh] object-contain rounded-lg shadow-2xl"
                />

                <div className="mt-3 px-4 py-2 rounded-lg bg-black/60 text-white text-sm sm:text-base font-medium text-center">
                  {selectedTechnicalPhoto.title}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* =========================
            GALERÍA
        ========================= */}
        <TabsContent
          value="gallery"
          className="p-6"
        >
          <SectionTitle
            icon={ImageIcon}
            title="Galería de maquinaria"
            description="Fotografías generales del equipo"
          />

          <label
            data-testid="upload-image-button"
            className="block w-full p-5 border-2 border-dashed border-slate-300 rounded-md text-center cursor-pointer hover:border-orange-500 hover:bg-orange-50/30 transition-colors mb-6"
          >

            <Upload
              size={28}
              className="mx-auto mb-2 text-slate-400"
            />

            <span className="text-sm text-slate-600">
              {uploadingImage
                ? 'Subiendo imagen...'
                : 'Agregar fotografía'}
            </span>

            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              disabled={uploadingImage}
              className="hidden"
            />

          </label>

          {machinery.images &&
          machinery.images.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">

              {machinery.images.map((img, idx) => (
                <div
                  key={idx}
                  className="relative group border border-slate-200 rounded-md overflow-hidden bg-slate-50"
                >

                  <img
                    src={img.startsWith("/uploads/")
                      ? `${process.env.REACT_APP_BACKEND_URL}${img}`
                      : img}
                    alt={`${machinery.name} ${idx + 1}`}
                    className="w-full h-56 object-cover"
                  />

                  <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    Fotografía {idx + 1}
                  </div>

                </div>
              ))}

            </div>
          ) : (
            <div className="text-center py-12 border border-slate-200 rounded-md bg-slate-50">

              <ImageIcon
                size={40}
                className="mx-auto text-slate-300 mb-3"
              />

              <p className="text-slate-500">
                No hay fotografías registradas
              </p>

            </div>
          )}

        </TabsContent>

      </Tabs>

      {/* =========================
          VISUALIZADOR DE PARTE DIARIO
      ========================= */}
      {reportDetailOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => setReportDetailOpen(false)}
        >
          <div
            className="w-full max-w-5xl max-h-[92vh] overflow-hidden rounded-xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* CABECERA */}
            <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-slate-200 bg-slate-50">
              <div>
                <h2 className="text-xl font-bold text-slate-800">
                  Parte Diario
                </h2>
                {selectedReport && (
                  <p className="text-sm text-slate-500 mt-1">
                    {machinery?.name || 'Maquinaria'} ·{' '}
                    {selectedReport.date
                      ? new Date(selectedReport.date).toLocaleDateString('es-BO')
                      : 'Sin fecha'}
                  </p>
                )}
              </div>

              <button
                type="button"
                onClick={() => setReportDetailOpen(false)}
                className="w-9 h-9 rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-100 hover:text-slate-800 text-xl"
                aria-label="Cerrar"
              >
                ×
              </button>
            </div>

            {/* CONTENIDO */}
            <div className="overflow-y-auto max-h-[calc(92vh-73px)] p-6">
              {loadingReportDetail ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="w-10 h-10 border-4 border-slate-200 border-t-orange-500 rounded-full animate-spin mb-4" />
                  <p className="text-slate-500">
                    Cargando parte completo...
                  </p>
                </div>
              ) : selectedReport ? (
                <div className="space-y-6">

                  {/* INFORMACIÓN GENERAL */}
                  <section>
                    <h3 className="text-sm font-bold uppercase tracking-wide text-slate-500 mb-3">
                      Información general
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                        <p className="text-xs text-slate-500">Fecha</p>
                        <p className="font-semibold text-slate-800 mt-1">
                          {selectedReport.date
                            ? new Date(selectedReport.date).toLocaleDateString('es-BO')
                            : '—'}
                        </p>
                      </div>

                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                        <p className="text-xs text-slate-500">Operador</p>
                        <p className="font-semibold text-slate-800 mt-1">
                          {getOperatorName(selectedReport.operator_id)}
                        </p>
                      </div>

                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                        <p className="text-xs text-slate-500">Tipo de trabajo</p>
                        <p className="font-semibold text-slate-800 mt-1">
                          {selectedReport.work_type || '—'}
                        </p>
                      </div>
                    </div>
                  </section>

                  {/* HORÓMETRO */}
                  <section>
                    <h3 className="text-sm font-bold uppercase tracking-wide text-slate-500 mb-3">
                      Horómetro
                    </h3>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="rounded-lg border border-slate-200 p-4">
                        <p className="text-xs text-slate-500">Inicial</p>
                        <p className="text-lg font-bold text-slate-800 mt-1">
                          {formatNumber(selectedReport.start_hours, 1)} hrs
                        </p>
                      </div>

                      <div className="rounded-lg border border-slate-200 p-4">
                        <p className="text-xs text-slate-500">Final</p>
                        <p className="text-lg font-bold text-slate-800 mt-1">
                          {formatNumber(selectedReport.end_hours, 1)} hrs
                        </p>
                      </div>

                      <div className="rounded-lg border border-orange-200 bg-orange-50 p-4">
                        <p className="text-xs text-orange-700">Horas trabajadas</p>
                        <p className="text-lg font-bold text-orange-800 mt-1">
                          {formatNumber(selectedReport.hours_worked, 1)} hrs
                        </p>
                      </div>
                    </div>
                  </section>

                  {/* COMBUSTIBLE */}
                  <section>
                    <h3 className="text-sm font-bold uppercase tracking-wide text-slate-500 mb-3">
                      Combustible
                    </h3>

                    <div className="rounded-lg border border-orange-200 bg-orange-50 p-4">
                      <p className="text-xs text-orange-700">
                        Combustible consumido
                      </p>
                      <p className="text-2xl font-bold text-orange-800 mt-1">
                        {formatNumber(selectedReport.diesel_consumed, 2)} L
                      </p>

                      {selectedReport.fuel_tank_id && (
                        <p className="text-xs text-orange-700 mt-2">
                          Tanque: {getFuelTankName(selectedReport.fuel_tank_id)}
                        </p>
                      )}
                    </div>
                  </section>

                  {/* LUBRICANTES Y FLUIDOS */}
                  <section>
                    <h3 className="text-sm font-bold uppercase tracking-wide text-slate-500 mb-3">
                      Lubricantes y fluidos
                    </h3>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[
                        ['Aceite motor', selectedReport.engine_oil_consumed],
                        ['Aceite transmisión', selectedReport.transmission_oil_consumed],
                        ['Aceite diferencial', selectedReport.differential_oil_consumed],
                        ['Aceite hidráulico', selectedReport.hydraulic_oil_consumed],
                        ['Refrigerante', selectedReport.coolant_consumed],
                        ['Grasa', selectedReport.grease_consumed],
                        ['Otros fluidos', selectedReport.other_fluid_consumed],
                        ['Aceite', selectedReport.oil_consumed]
                      ].map(([label, value]) => (
                        <div
                          key={label}
                          className="rounded-lg border border-slate-200 p-3"
                        >
                          <p className="text-xs text-slate-500">{label}</p>
                          <p className="font-semibold text-slate-800 mt-1">
                            {formatNumber(value ?? 0, 2)} L
                          </p>
                        </div>
                      ))}
                    </div>
                  </section>

                  {/* OPERACIÓN */}
                  <section>
                    <h3 className="text-sm font-bold uppercase tracking-wide text-slate-500 mb-3">
                      Operación
                    </h3>

                    <div className="space-y-3">
                      <div className="rounded-lg border border-slate-200 p-4">
                        <p className="text-xs text-slate-500 mb-1">
                          Trabajo realizado
                        </p>
                        <p className="text-sm text-slate-800 whitespace-pre-wrap">
                          {selectedReport.work_description || '—'}
                        </p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="rounded-lg border border-slate-200 p-4">
                          <p className="text-xs text-slate-500">Ubicación</p>
                          <p className="text-sm font-medium text-slate-800 mt-1">
                            {selectedReport.location || '—'}
                          </p>
                        </div>

                        <div className="rounded-lg border border-slate-200 p-4">
                          <p className="text-xs text-slate-500">Estado de la máquina</p>
                          <p className="text-sm font-medium text-slate-800 mt-1">
                            {selectedReport.machine_status || '—'}
                          </p>
                        </div>
                      </div>

                      <div className="rounded-lg border border-slate-200 p-4">
                        <p className="text-xs text-slate-500 mb-1">
                          Observaciones
                        </p>
                        <p className="text-sm text-slate-800 whitespace-pre-wrap">
                          {selectedReport.observations || '—'}
                        </p>
                      </div>
                    </div>
                  </section>

                  {/* CHECKLIST */}
                  <section>
                    <h3 className="text-sm font-bold uppercase tracking-wide text-slate-500 mb-3">
                      Checklist
                    </h3>

                    {Array.isArray(selectedReport.checklist) &&
                    selectedReport.checklist.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {selectedReport.checklist.map((item, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700"
                          >
                            <span className="text-green-600">✓</span>
                            {item}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500">
                        No hay elementos de checklist registrados.
                      </p>
                    )}
                  </section>

                  {/* NOVEDADES / PARADAS */}
                  <section>
                    <h3 className="text-sm font-bold uppercase tracking-wide text-slate-500 mb-3">
                      Novedades / Paradas
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="rounded-lg border border-slate-200 p-4">
                        <p className="text-xs text-slate-500">Hubo parada</p>
                        <p className="font-semibold text-slate-800 mt-1">
                          {selectedReport.has_downtime ? 'Sí' : 'No'}
                        </p>
                      </div>

                      <div className="rounded-lg border border-slate-200 p-4">
                        <p className="text-xs text-slate-500">Horas de parada</p>
                        <p className="font-semibold text-slate-800 mt-1">
                          {formatNumber(selectedReport.downtime_hours ?? 0, 2)} hrs
                        </p>
                      </div>

                      <div className="rounded-lg border border-slate-200 p-4">
                        <p className="text-xs text-slate-500">Motivo</p>
                        <p className="font-semibold text-slate-800 mt-1">
                          {selectedReport.downtime_reason || '—'}
                        </p>
                      </div>
                    </div>
                  </section>

                  {/* FOTOGRAFÍAS */}
                  <section>
                    <h3 className="text-sm font-bold uppercase tracking-wide text-slate-500 mb-3">
                      Evidencia fotográfica
                    </h3>

                    {[
                      ['Foto del horómetro', selectedReport.horometer_photo],
                      ['Foto del trabajo', selectedReport.work_photo],
                      ...(Array.isArray(selectedReport.images)
                        ? selectedReport.images.map((img, index) => [
                            `Fotografía ${index + 1}`,
                            img
                          ])
                        : [])
                    ].filter(([, src]) => src).length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {[
                          ['Foto del horómetro', selectedReport.horometer_photo],
                          ['Foto del trabajo', selectedReport.work_photo],
                          ...(Array.isArray(selectedReport.images)
                            ? selectedReport.images.map((img, index) => [
                                `Fotografía ${index + 1}`,
                                img
                              ])
                            : [])
                        ].filter(([, src]) => src).map(([label, src], index) => (
                          <div
                            key={`${label}-${index}`}
                            className="rounded-lg border border-slate-200 overflow-hidden bg-slate-50"
                          >
                            <div className="px-3 py-2 border-b border-slate-200 text-xs font-semibold text-slate-600">
                              {label}
                            </div>

                            <img
                              src={src.startsWith('/uploads/')
                                ? `${process.env.REACT_APP_BACKEND_URL}${src}`
                                : src}
                              alt={label}
                              className="w-full h-64 object-contain bg-slate-100"
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-8 text-center">
                        <ImageIcon
                          size={32}
                          className="mx-auto text-slate-300 mb-2"
                        />
                        <p className="text-sm text-slate-500">
                          Este parte no tiene fotografías registradas.
                        </p>
                      </div>
                    )}
                  </section>

                  {/* IDENTIFICACIÓN DEL REGISTRO */}
                  <section className="pt-2 border-t border-slate-200">
                    <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-slate-500">
                      <span>
                        ID: {selectedReport.id}
                      </span>

                      {selectedReport.created_at && (
                        <span>
                          Registrado:{' '}
                          {new Date(selectedReport.created_at).toLocaleString('es-BO')}
                        </span>
                      )}

                      {selectedReport.updated_at && (
                        <span>
                          Actualizado:{' '}
                          {new Date(selectedReport.updated_at).toLocaleString('es-BO')}
                        </span>
                      )}
                    </div>
                  </section>

                </div>
              ) : (
                <div className="py-16 text-center text-slate-500">
                  No se pudo cargar el parte.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MachineryDetail;
